import { useLayoutEffect, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { AlertTriangle } from "lucide-react"

/**
 * Plattformweites Stoerungs-Banner — auf JEDER Seite oben, nicht wegklickbar.
 *
 * Anlass 27.08.2026: Der Telefonbot crashte seit dem Vorabend bei jedem
 * Sanitec-Anruf, und niemand hat es gemerkt — die Info stand nur im
 * KI-Telefon-Dashboard, das gerade keiner offen hatte. Eine Stoerung dieser
 * Klasse muss den Nutzer finden, nicht umgekehrt.
 *
 * Bewusst KEIN X zum Wegklicken: das Banner verschwindet, wenn die Stoerung
 * behoben ist — vorher soll es stoeren, das ist sein Zweck. Quelle:
 * /api/v1/stoerungen (60s-Poll, nur bei aktivem Tab).
 */

type Stoerung = {
  check: string
  titel: string
  message: string
  seit?: string
}

/** Der eine Satz, der sagt, was die Stoerung fuer uns heisst — bei den
 *  Anbieter-Meldungen ist das der letzte („Anrufe erreichen den Telefonbot
 *  moeglicherweise nicht."), davor steht der Rohtext des Anbieters. */
export function kurzfassung(message: string): string {
  const saetze = (message || "").split(/(?<=\.)\s+/).map((t) => t.trim()).filter(Boolean)
  return saetze.length ? saetze[saetze.length - 1] : (message || "")
}

export function StoerungsBanner() {
  const q = useQuery({
    queryKey: ["stoerungen"],
    queryFn: async (): Promise<{ stoerungen: Stoerung[] }> => {
      const r = await fetch("/api/v1/stoerungen", { credentials: "include" })
      // Fehler beim Abruf ist KEINE Stoerung — sonst meldet das Banner sich
      // selbst (z.B. waehrend eines Deploys oder bei abgelaufener Session).
      if (!r.ok) return { stoerungen: [] }
      return r.json()
    },
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 0,
  })

  const items = q.data?.stoerungen || []
  const bar = useRef<HTMLDivElement>(null)

  // 🔴 Banner-Hoehe als CSS-Variable `--stoerung-h` (#165, 08.10.2026): Der
  // Platzhalter unten schiebt nur den normalen Seitenfluss. Alles, was selbst
  // fest positioniert ist — Seitenleiste, Suche, „Interne Aufgabe", Problem
  // melden, Live-Anruf —, lag unter dem Banner und war nicht klickbar. Diese
  // Elemente rechnen die Variable in ihr `top` ein.
  useLayoutEffect(() => {
    const wurzel = document.documentElement
    const el = bar.current
    if (!el) {
      wurzel.style.setProperty("--stoerung-h", "0px")
      return
    }
    const setzen = () => wurzel.style.setProperty("--stoerung-h", `${el.offsetHeight}px`)
    setzen()
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(setzen) : null
    ro?.observe(el)
    return () => {
      ro?.disconnect()
      wurzel.style.setProperty("--stoerung-h", "0px")
    }
  }, [items.length])

  if (!items.length) return null

  return (
    <>
      {/* 🔑 Platzhalter in gleicher Hoehe: das Banner liegt fest oben und
          wuerde sonst den Seitenkopf verdecken — ausgerechnet die Zeile mit
          Mandant und Navigation. Ein Banner, das etwas anderes zudeckt,
          erzeugt das naechste Problem. */}
      <div style={{ height: "var(--stoerung-h, 0px)" }} />
      <div
        ref={bar}
        id="stoerungs-banner"
        role="alert"
        aria-live="assertive"
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 200,
          background: "#b91c1c", color: "#fff",
          boxShadow: "0 3px 12px rgba(185,28,28,0.4)",
          borderBottom: "2px solid #7f1d1d",
          padding: "6px 16px",
          display: "flex", flexDirection: "column", gap: 4,
        }}
      >
        {items.map((s) => (
          /* #109 (Paul, 17.09.2026): „geht diese Warnung noch schmaler? rot
             würde ja schon Alarm zeigen". Eine Zeile: Titel + der Satz, der
             sagt, was es fuer uns heisst. Der ganze Text bleibt erreichbar —
             ein Klick auf die Zeile klappt ihn auf (native <details>, kein
             Schliessen-Knopf: das Banner geht erst weg, wenn die Stoerung
             behoben ist). */
          <details key={s.check} style={{ minWidth: 0 }}>
            <summary style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", listStyle: "none", minWidth: 0 }}>
              <AlertTriangle style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" }}>
                Störung: {s.titel}
              </span>
              <span style={{ fontSize: 13, opacity: 0.95, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {kurzfassung(s.message)}
              </span>
              {s.seit && (
                <span style={{ fontSize: 12, opacity: 0.8, whiteSpace: "nowrap", marginLeft: "auto" }}>
                  seit {s.seit}
                </span>
              )}
            </summary>
            {/* 🔴 Nicht abschneiden: der Volltext steht hier ungekuerzt und
                umgebrochen — nur eben erst nach dem Aufklappen. */}
            <div style={{ fontSize: 13, lineHeight: 1.45, opacity: 0.95, padding: "4px 0 2px 28px" }}>
              {s.message}
            </div>
          </details>
        ))}
      </div>
    </>
  )
}
